<template>
  <!-- GREY TABBAR MODULE START -->
  <div class="tw-w-full tw-py-3.5 lg:tw-py-2 tw-bg-gray-50">
    <nav
      ref="navRef"
      aria-label="workperks-categories-nav"
      :class="['tw-container tw-mx-auto tw-pl-8 lg:tw-px-8 pr-2 tw-overflow-hidden tw-flex tw-flex-col md:tw-flex-row md:tw-items-center', (data.enableGiftCards || data.enableWeeklyFlyers) && 'tw-gap-y-4']"
    >
      <ul
        ref="navListRef"
        :class="[
          'tw-flex tw-overflow-x-scroll xl:tw-justify-between tw-gap-x-4 lg:tw-gap-x-1.5 lg:tw-text-sm tw-font-semibold tw-text-gray-600 no-scrollbar',
          overflowItems.length ? 'tw-flex-initial tw-min-w-0' : 'tw-flex-auto'
        ]"
      >
        <li ref="homeTabRef">
          <a
            href="/home"
            id="home-tab"
            :class="[
              'tw-h-full tw-px-2.5 tw-flex tw-items-center tw-justify-center tw-rounded-md hover:tw-bg-gray-200 focus:tw-bg-gray-200 focus:tw-outline-none',
              data.homepage
                && 'tw-bg-gray-200'
            ]"
          >
            <i
              class="material-icons tw-text-base tw-leading-none lg:tw-text-display-xs"
              >home</i
            >
          </a>
        </li>

        <li
          v-for="(item, index) in visibleItems"
          :key="index"
        >
          <button
            @click.prevent="itemDidClick(item)"
            :class="[
              'tw-py-1 tw-px-2.5 lg:tw-px-3 tw-h-full tw-w-max tw-whitespace-nowrap tw-rounded-md hover:tw-bg-gray-200 focus:tw-bg-gray-200 lg:tw-relative',
              selectedItem && Number(selectedItem.id) === item.id
                && 'tw-bg-gray-200',
                hasTopPadding ? 'lg:tw-pb-2 lg:tw-pt-3.5' : 'lg:tw-py-2'
            ]"
          >
            <span v-if="shouldHaveNewLabel(item)" class="tw-text-xs tw-font-semibold tw-text-primary-600 tw-align-middle lg:tw-absolute lg:tw-right-3 lg:tw-top-1.5 lg:tw-leading-none tw-mr-1 lg:tw-m-0">{{ localizedLabel }}</span>
            {{ localizedName(item.name) }}
          </button>
        </li>

        <li
          v-if="overflowItems.length"
          ref="moreMenuRootRef"
        >
          <button
            ref="moreButtonRef"
            type="button"
            @click.prevent="toggleMoreMenu"
            aria-haspopup="true"
            :aria-expanded="showMoreMenu"
            aria-label="More categories"
            :class="[
              'tw-py-1 tw-px-2.5 lg:tw-px-3 tw-h-full tw-w-max tw-whitespace-nowrap tw-rounded-md tw-flex tw-items-center tw-gap-x-0.5 hover:tw-bg-gray-200 focus:tw-bg-gray-200 focus:tw-outline-none',
              hasTopPadding ? 'lg:tw-pb-2 lg:tw-pt-3.5' : 'lg:tw-py-2'
            ]"
          >
            {{ localizedMoreLabel }}
            <i class="material-icons tw-text-base tw-leading-none">expand_more</i>
          </button>
        </li>
      </ul>

      <!-- Hidden measurement clone: mirrors the full item list (and the "More" button)
           off-screen so we can measure each item's natural width without it affecting
           the visible layout, even for items currently collapsed into the "More" menu. -->
      <ul
        aria-hidden="true"
        class="tw-flex tw-gap-x-4 lg:tw-gap-x-1.5 lg:tw-text-sm tw-font-semibold tw-whitespace-nowrap tw-invisible tw-absolute tw-pointer-events-none"
        style="top: -9999px; left: -9999px;"
      >
        <li
          v-for="(item, index) in items"
          :key="index"
          ref="measureItemRefs"
        >
          <button class="tw-py-1 tw-px-2.5 lg:tw-px-3 tw-h-full tw-w-max tw-whitespace-nowrap tw-rounded-md">
            <span v-if="shouldHaveNewLabel(item)" class="tw-text-xs tw-font-semibold tw-mr-1">{{ localizedLabel }}</span>
            {{ localizedName(item.name) }}
          </button>
        </li>
        <li ref="measureMoreButtonRef">
          <button class="tw-py-1 tw-px-2.5 lg:tw-px-3 tw-h-full tw-w-max tw-whitespace-nowrap tw-rounded-md tw-flex tw-items-center tw-gap-x-0.5">
            {{ localizedMoreLabel }}
            <i class="material-icons tw-text-base tw-leading-none">expand_more</i>
          </button>
        </li>
      </ul>

      <span
        v-if="data.userLocation['country'] != 'US' && (data.enableGiftCards || data.enableWeeklyFlyers)"
        ref="dividerRef"
        class="tw-hidden md:tw-inline-flex tw-w-px tw-h-9 tw-bg-gray-400 lg:tw-bg-gray-200 tw-mx-4"
      ></span>
      <div
        ref="buttonsGroupRef"
        :class="['tw-flex tw-items-center tw-w-full md:tw-w-auto md:tw-flex-shrink-0 tw-pr-8 lg:tw-pr-0 tw-gap-2', data.locale === 'fr' ? 'tw-flex-col md:tw-flex-row' : 'tw-flex-row']"
      >
        <GiftCardButton
          v-if="data.userLocation['country'] != 'US' && data.enableGiftCards"
          class="tw-justify-center tw-w-full"
        />
        <WeeklyFlyerButton
          v-if="data.userLocation['country'] != 'US' && data.enableWeeklyFlyers"
        />
      </div>
    </nav>
  </div>

  <!-- Teleported so the dropdown escapes the nav's scroll/clip containers. -->
  <Teleport to="body">
    <ul
      v-if="showMoreMenu"
      ref="moreMenuRef"
      role="menu"
      :style="{ top: dropdownPosition.top + 'px', left: dropdownPosition.left + 'px' }"
      class="tw-fixed tw-z-50 tw-min-w-[10rem] tw-max-w-xs tw-bg-white tw-rounded-md tw-shadow-lg tw-border tw-border-gray-200 tw-py-1"
    >
      <li
        v-for="(item, index) in overflowItems"
        :key="index"
        role="menuitem"
      >
        <button
          type="button"
          @click.prevent="itemDidClick(item); showMoreMenu = false"
          :class="[
            'tw-w-full tw-text-left tw-px-4 tw-py-2 tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-text-gray-600 hover:tw-bg-gray-100 focus:tw-bg-gray-100 focus:tw-outline-none',
            selectedItem && Number(selectedItem.id) === item.id
              && 'tw-bg-gray-100'
          ]"
        >
          <span v-if="shouldHaveNewLabel(item)" class="tw-text-xs tw-font-semibold tw-text-primary-600 tw-mr-1">{{ localizedLabel }}</span>
          {{ localizedName(item.name) }}
        </button>
      </li>
    </ul>
  </Teleport>
  <!-- GREY TABBAR MODULE END -->
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import GiftCardButton from "components/workperks/GiftCardButton.vue";
import WeeklyFlyerButton from "components/workperks/WeeklyFlyerButton.vue";
import User from "lib/User";
import { Category, CategoryName } from "@/lib/workperks/models/Category";

const props = defineProps({
  items: {
    required: true,
    type: Array as () => Category[],
  },
  selectedItem: {
    required: false,
    type: Object as () => Category | null,
  },
});

const emit = defineEmits(["list-item-was-selected"]);

const data = reactive({
  locale: window.l.locale,
  homepage: false,
  userLocation: null as any,
  enableGiftCards: window.s.site.enableGiftCards ?? false,
  enableWeeklyFlyers: window.s.site.enableWeeklyFlyers ?? false,
  enableTickets: window.s.site.enableTickets ?? false,
  enableTravelWidget: window.s.site.enableTravelWidget ?? false,
});

const hasTopPadding = computed(() => data.enableTickets || data.enableTravelWidget);

const shouldHaveNewLabel = (item: Category) =>
  (item.id === 6 && data.enableTickets) || (item.id === 7 && data.enableTravelWidget);
const localizedLabel = computed(() => data.locale === "fr" ? "Nouveau" : "New");
const localizedMoreLabel = computed(() => data.locale === "fr" ? "Plus" : "More");

// Overflow-into-"More" handling: whenever the category row doesn't have room
// for every item, the items that don't fit are collapsed into a "More"
// dropdown instead of relying on horizontal scroll.
const FALLBACK_MORE_BUTTON_WIDTH = 90;
// Matches the `md:tw-flex-row` breakpoint below — the row/column layout switch.
const ROW_LAYOUT_BREAKPOINT = 768;

const navRef = ref<HTMLElement | null>(null);
const navListRef = ref<HTMLElement | null>(null);
const homeTabRef = ref<HTMLElement | null>(null);
const dividerRef = ref<HTMLElement | null>(null);
const buttonsGroupRef = ref<HTMLElement | null>(null);
const moreButtonRef = ref<HTMLElement | null>(null);
const moreMenuRootRef = ref<HTMLElement | null>(null);
const moreMenuRef = ref<HTMLElement | null>(null);
const measureItemRefs = ref<HTMLElement[]>([]);
const measureMoreButtonRef = ref<HTMLElement | null>(null);

const showMoreMenu = ref(false);
const dropdownPosition = ref({ top: 0, left: 0 });
const visibleCount = ref(props.items.length);

const visibleItems = computed(() => props.items.slice(0, visibleCount.value));
const overflowItems = computed(() => props.items.slice(visibleCount.value));


const recalculateVisibleCount = () => {
  if (!navRef.value || !homeTabRef.value || measureItemRefs.value.length !== props.items.length) {
    return;
  }

  let containerWidth = navRef.value.clientWidth;
  if (window.innerWidth >= ROW_LAYOUT_BREAKPOINT) {
    if (dividerRef.value) {
      const dividerStyle = getComputedStyle(dividerRef.value);
      containerWidth -= dividerRef.value.offsetWidth
        + parseFloat(dividerStyle.marginLeft || "0")
        + parseFloat(dividerStyle.marginRight || "0");
    }
    if (buttonsGroupRef.value) {
      containerWidth -= buttonsGroupRef.value.offsetWidth;
    }
  }

  const gap = parseFloat(getComputedStyle(navListRef.value!).columnGap) || 0;
  const homeWidth = homeTabRef.value.offsetWidth + gap;
  const itemWidths = measureItemRefs.value.map((el) => el.offsetWidth + gap);
  const totalWidth = homeWidth + itemWidths.reduce((sum, width) => sum + width, 0);

  if (totalWidth <= containerWidth) {
    visibleCount.value = props.items.length;
    return;
  }

  const moreButtonWidth = (measureMoreButtonRef.value?.offsetWidth ?? FALLBACK_MORE_BUTTON_WIDTH) + gap;
  let used = homeWidth + moreButtonWidth;
  let count = 0;
  for (const width of itemWidths) {
    if (used + width > containerWidth) {
      break;
    }
    used += width;
    count++;
  }
  visibleCount.value = count;
};

let resizeHandle: number | undefined;
const scheduleRecalculate = () => {
  showMoreMenu.value = false;
  if (resizeHandle !== undefined) {
    window.clearTimeout(resizeHandle);
  }
  resizeHandle = window.setTimeout(recalculateVisibleCount, 100);
};

const VIEWPORT_EDGE_PADDING = 8;

const toggleMoreMenu = async () => {
  if (showMoreMenu.value) {
    showMoreMenu.value = false;
    return;
  }
  if (!moreButtonRef.value) {
    return;
  }

  const buttonRect = moreButtonRef.value.getBoundingClientRect();
  dropdownPosition.value = { top: buttonRect.bottom + 4, left: buttonRect.left };
  showMoreMenu.value = true;

  // The menu's real size is only known once it's rendered, so position it
  // first, then nudge it back on-screen if it overflows the viewport.
  await nextTick();
  if (!moreMenuRef.value) {
    return;
  }
  const menuRect = moreMenuRef.value.getBoundingClientRect();

  let { top, left } = dropdownPosition.value;
  const maxLeft = window.innerWidth - menuRect.width - VIEWPORT_EDGE_PADDING;
  if (left > maxLeft) {
    left = Math.max(VIEWPORT_EDGE_PADDING, maxLeft);
  }
  if (buttonRect.bottom + 4 + menuRect.height > window.innerHeight) {
    top = Math.max(VIEWPORT_EDGE_PADDING, buttonRect.top - menuRect.height - 4);
  }
  dropdownPosition.value = { top, left };
};

const closeMoreMenuOnOutsideClick = (event: MouseEvent) => {
  const target = event.target as Node;
  const clickedButton = moreMenuRootRef.value?.contains(target);
  const clickedMenu = moreMenuRef.value?.contains(target);
  if (!clickedButton && !clickedMenu) {
    showMoreMenu.value = false;
  }
};

const closeMoreMenuOnEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    showMoreMenu.value = false;
    moreButtonRef.value?.focus();
  }
};

const closeMoreMenuOnScroll = () => {
  showMoreMenu.value = false;
};

watch(showMoreMenu, (isOpen) => {
  if (isOpen) {
    document.addEventListener("click", closeMoreMenuOnOutsideClick);
    document.addEventListener("keydown", closeMoreMenuOnEscape);
    window.addEventListener("scroll", closeMoreMenuOnScroll, true);
  } else {
    document.removeEventListener("click", closeMoreMenuOnOutsideClick);
    document.removeEventListener("keydown", closeMoreMenuOnEscape);
    window.removeEventListener("scroll", closeMoreMenuOnScroll, true);
  }
});

watch(() => props.items, () => {
  nextTick(recalculateVisibleCount);
});

onMounted(() => {
  nextTick(recalculateVisibleCount);
  window.addEventListener("resize", scheduleRecalculate);
});

onUnmounted(() => {
  window.removeEventListener("resize", scheduleRecalculate);
  document.removeEventListener("click", closeMoreMenuOnOutsideClick);
  document.removeEventListener("keydown", closeMoreMenuOnEscape);
  window.removeEventListener("scroll", closeMoreMenuOnScroll, true);
  if (resizeHandle !== undefined) {
    window.clearTimeout(resizeHandle);
  }
});

const localizedName = (object: string | CategoryName) => {
  if (typeof object !== "string" && !(object instanceof String)) {
    if ("en" in object || "fr" in object) {
      return data.locale === "en" ? object.en : object.fr;
    }
  }
  return object;
};

const itemDidClick = (item: Category) => {
  data.homepage = false;
  emit("list-item-was-selected", item);
};

// created logic
const headerPath = window.location.pathname;
data.userLocation = User.getCurrent().primaryLocation;
if (headerPath === "/home") {
  data.homepage = true;
}

defineExpose({
  localizedName,
  itemDidClick,
});
</script>

<style>
.no-scrollbar {
  overflow-y: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.no-scrollbar::-webkit-scrollbar {
  /* WebKit */
  width: 0;
  height: 0;
}
</style>
